/**
 * Canonical SEO page manifest — single source of truth.
 *
 * Every future indexable public page (homepage, category, guide, buyer
 * or seller landing) MUST be declared here.  Downstream systems read
 * from this manifest:
 *
 *   • the dynamic sitemap generator (backend `/api/sitemap-*.xml`)
 *   • the SEO test suite (title/description uniqueness, canonical prefix,
 *     no preview URL leak, no noindex-in-sitemap)
 *   • the internal-link resolver (related-content blocks)
 *   • the entity graph builder (BreadcrumbList JSON-LD)
 *
 * Rules:
 *   • Paths are absolute site-relative (start with "/") and canonical.
 *   • Titles are 45–65 chars, unique, and end with "| Vanturis".
 *   • Descriptions are 130–160 chars, unique, factually accurate.
 *   • `indexable=true` pages must not carry a `noindex` directive at
 *     render time (guarded by SEO.jsx and enforced by the test suite).
 *   • `status="live"` pages appear in the active sitemap; `"planned"`
 *     pages are held out until content is authored + approved.
 *   • Section values keep pages grouped for the sitemap index +
 *     internal-link blocks: "brand" | "category" | "guide" | "policy"
 *     | "seller" | "buyer" | "trust".
 */

export const SEO_PAGES = [
  // ── Brand surface ────────────────────────────────────────────────
  {
    path: "/",
    title: "Vanturis | Marketplace · Passport · Auction",
    description:
      "Vanturis is a premium marketplace for luxury motorcoaches, Prevost motorhomes, entertainer coaches, Class A and Super C motorhomes, overland vehicles and stacker trailers.",
    section: "brand",
    priority: 1.0,
    changefreq: "weekly",
    indexable: true,
    status: "live",
  },
  {
    path: "/about",
    title: "About Vanturis | Premium Motorcoach Marketplace",
    description:
      "Learn how Vanturis is building a premium motorcoach, Prevost and specialty vehicle marketplace with editorial presentation, verified sellers and standardized listing data.",
    section: "brand",
    priority: 0.7,
    changefreq: "monthly",
    indexable: true,
    status: "live",
  },
  {
    path: "/contact",
    title: "Contact Vanturis | Motorcoach Marketplace Inquiries",
    description:
      "Contact the Vanturis team about premium motorcoach and specialty vehicle listings, seller onboarding, buyer support, media inquiries and partner opportunities.",
    section: "brand",
    priority: 0.5,
    changefreq: "monthly",
    indexable: true,
    status: "live",
  },
  {
    path: "/legal/privacy",
    title: "Privacy Policy | Vanturis",
    description:
      "How Vanturis collects, uses and protects information across the premium motorcoach and specialty vehicle marketplace.",
    section: "policy",
    priority: 0.3,
    changefreq: "yearly",
    indexable: true,
    status: "live",
  },
  {
    path: "/legal/terms",
    title: "Terms of Use | Vanturis",
    description:
      "The terms that govern use of the Vanturis premium motorcoach and specialty vehicle marketplace and its educational content.",
    section: "policy",
    priority: 0.3,
    changefreq: "yearly",
    indexable: true,
    status: "live",
  },
  {
    path: "/legal/listing-disclaimer",
    title: "Listing & Information Disclaimer | Vanturis",
    description:
      "The information and listing disclaimer governing specifications, condition, mileage, pricing and availability shown across the Vanturis marketplace.",
    section: "policy",
    priority: 0.3,
    changefreq: "yearly",
    indexable: true,
    status: "live",
  },
  {
    // Vanturis Passport product marketing page (Phase 8).  Held at
    // "planned" so the Production sitemap is untouched until the
    // owner approves Passport public canonicals (future
    // passport.vanturis.com).  Marketing content only — no private
    // Passport data renders on this route.
    path: "/passport",
    title: "Vanturis Passport | Permanent History for Luxury Coaches",
    description:
      "Vanturis Passport is a permanent, VIN-anchored history for luxury motorcoaches and specialty vehicles: ownership, service, inspections, documents and controlled sharing.",
    section: "trust",
    priority: 0.7,
    changefreq: "monthly",
    indexable: true,
    status: "planned",
  },

  // ── Public marketplace + core content pages (indexable post-launch) ──
  {
    path: "/marketplace",
    title: "Motorcoach Marketplace | Vanturis",
    description:
      "Browse curated luxury motorcoach and premium touring listings on Vanturis — Prevost motorhomes, entertainer coaches and specialty vehicles from verified sellers.",
    section: "brand",
    priority: 0.95,
    changefreq: "daily",
    indexable: true,
    status: "live",
  },
  {
    path: "/vanturis-trust",
    title: "Vanturis Trust System | Verified, Certified & Passport",
    description:
      "The Vanturis Trust System — Verified, Certified, Passport, Score and Value — the layered checks that support every premium motorcoach listing on the marketplace.",
    section: "trust",
    priority: 0.8,
    changefreq: "monthly",
    indexable: true,
    status: "live",
  },
  {
    path: "/vanturis-standards",
    title: "Vanturis Standards & Classification System",
    description:
      "How Vanturis classifies premium motorcoaches, Prevost motorhomes, entertainer coaches and specialty vehicles — the standards behind every listing on the marketplace.",
    section: "trust",
    priority: 0.7,
    changefreq: "monthly",
    indexable: true,
    status: "live",
  },
  {
    path: "/collections",
    title: "Vanturis Collections | Curated Motorcoach Categories",
    description:
      "Explore Vanturis Collections — curated motorcoach and specialty vehicle categories, from Prevost motorhomes to entertainer coaches and expedition vehicles.",
    section: "brand",
    priority: 0.8,
    changefreq: "weekly",
    indexable: true,
    status: "live",
  },
  {
    path: "/why-list-with-vanturis",
    title: "Why List Your Motorcoach with Vanturis | Premium Coach Marketplace",
    description:
      "Why sellers list premium motorcoaches, Prevost motorhomes, entertainer coaches and specialty vehicles on Vanturis — editorial presentation, verified buyers and standardized data.",
    section: "seller",
    priority: 0.8,
    changefreq: "monthly",
    indexable: true,
    status: "live",
  },

  // ── Category landing pages ───────────────────────────────────────
  { path: "/prevost-motorhomes",     section: "category", status: "live",     indexable: true,
    priority: 0.9, changefreq: "weekly",
    title: "Prevost Motorhomes for Sale | Vanturis",
    description: "Explore Prevost motorhomes listed on Vanturis. Compare Prevost XL, XLII (XL2) and H3-45 platforms, learn what to inspect, understand value factors, and browse available luxury coach listings." },
  { path: "/entertainer-coaches",    section: "category", status: "planned",
    title: "Entertainer Coaches for Sale | Vanturis",
    description: "Professional entertainer coaches — 12-bunk, 14-bunk and executive floorplans — listed on the Vanturis premium marketplace with detailed specifications." },
  { path: "/luxury-motorcoaches",    section: "category", status: "planned",
    title: "Luxury Motorcoaches for Sale | Vanturis",
    description: "Premium luxury motorcoaches from Marathon, Liberty, Millennium, Newell, Featherlite and other converters — presented on the Vanturis marketplace." },
  { path: "/class-a-motorhomes",     section: "category", status: "planned",
    title: "Luxury Class A Motorhomes for Sale | Vanturis",
    description: "High-end Class A diesel pushers — Foretravel, Country Coach, Tiffin Zephyr, Newmar King Aire and American Coach — on the Vanturis marketplace." },
  { path: "/super-c-motorhomes",     section: "category", status: "planned",
    title: "Super C Motorhomes for Sale | Vanturis",
    description: "Renegade, Dynamax, Newmar Super Star and other premium Super C motorhomes on Freightliner, Peterbilt and Kenworth chassis — via Vanturis." },
  { path: "/overland-vehicles",      section: "category", status: "planned",
    title: "Overland & Expedition Vehicles for Sale | Vanturis",
    description: "EarthRoamer, GXV, Unicat, Bliss Mobil and other purpose-built expedition platforms — presented on the Vanturis premium marketplace." },
  { path: "/specialty-trailers",     section: "category", status: "planned",
    title: "Specialty Trailers for Sale | Vanturis",
    description: "Merchandise, equipment, production, hospitality and wardrobe trailers built for professional touring — listed on Vanturis." },
  { path: "/stacker-trailers",       section: "category", status: "planned",
    title: "Stacker Trailers for Sale | Vanturis",
    description: "Premium stacker trailers for professional racing, touring and hospitality operations — listed on the Vanturis specialty trailer marketplace." },
  { path: "/marketplace/private-aviation", section: "category", status: "live", indexable: true,
    priority: 0.9, changefreq: "weekly",
    title: "Private Jets & Aircraft | Vanturis Aviation",
    description: "Vanturis Aviation connects buyers and sellers of private jets, business aircraft and turboprops through trusted aviation specialists — qualified introductions, partner listings and auction opportunities across the Vanturis luxury travel ecosystem." },
  { path: "/marketplace/luxury-marine", section: "category", status: "live", indexable: true,
    priority: 0.9, changefreq: "weekly",
    title: "Luxury Yachts & Motor Yachts | Vanturis Marine",
    description: "Vanturis Marine connects buyers and sellers of luxury motor yachts, superyachts and premium vessels through trusted marine specialists — qualified introductions, partner inventory and auction opportunities across the Vanturis luxury travel ecosystem." },

  // ── Buyer / Seller landing (planned) ─────────────────────────────
  { path: "/buyers",  section: "buyer",  status: "planned",
    title: "Buy a Premium Motorcoach or Specialty Vehicle | Vanturis",
    description: "How to shop, inspect and acquire a premium motorcoach, entertainer coach or overland vehicle through the Vanturis marketplace and concierge." },
  { path: "/sellers", section: "seller", status: "planned",
    title: "Sell Your Motorcoach or Specialty Vehicle on Vanturis",
    description: "List your Prevost motorhome, entertainer coach, luxury motorcoach, Class A, Super C, overland vehicle or specialty trailer on the Vanturis premium marketplace." },
  { path: "/how-it-works",          section: "brand",  status: "planned",
    title: "How Vanturis Works | The Motorcoach Marketplace",
    description: "How Vanturis connects buyers with verified sellers of premium motorcoaches, entertainer coaches, motorhomes, overland vehicles and specialty trailers." },
  { path: "/trust-and-verification", section: "trust", status: "planned",
    title: "Vanturis Trust & Verification System",
    description: "Vanturis Verified, Certified, Passport, Score and Value — the layered trust system supporting every premium motorcoach and specialty vehicle listing." },
  { path: "/listing-standards",     section: "trust",  status: "planned",
    title: "Vanturis Listing Standards | Premium Marketplace",
    description: "The listing standards Vanturis applies to every published motorcoach, entertainer coach, motorhome, overland vehicle and specialty trailer." },
  { path: "/editorial-policy",      section: "trust",  status: "planned",
    title: "Editorial & Content Policy | Vanturis",
    description: "How Vanturis researches, sources, reviews and updates its motorcoach, Prevost, entertainer coach and specialty vehicle content." },

  // ── First 12 guides — priority release plan ──────────────────────
  { path: "/guides/what-is-a-prevost-motorhome",          section: "guide", status: "planned",
    title: "What Is a Prevost Motorhome? | Vanturis Guide",
    description: "A Prevost motorhome is a luxury coach built on a Prevost bus chassis and converted by a specialist coachbuilder. Learn what defines a Prevost motorhome." },
  { path: "/guides/prevost-motorhome-buying-guide",       section: "guide", status: "planned",
    title: "Prevost Motorhome Buying Guide | Vanturis",
    description: "Chassis, converter, floorplan, mileage, service history and documentation — the Vanturis Prevost motorhome buying guide for premium coach buyers." },
  { path: "/guides/what-is-an-entertainer-coach",         section: "guide", status: "planned",
    title: "What Is an Entertainer Coach? | Vanturis Guide",
    description: "An entertainer coach is a Prevost or similar chassis converted with bunks and touring amenities for musicians, artists, crews and executive teams." },
  { path: "/guides/entertainer-coach-buying-guide",       section: "guide", status: "planned",
    title: "Entertainer Coach Buying Guide | Vanturis",
    description: "12-bunk vs 14-bunk, converter, chassis, generator hours, service history — the Vanturis entertainer coach buying guide for touring buyers." },
  { path: "/guides/motorcoach-prepurchase-inspection",    section: "guide", status: "planned",
    title: "Motorcoach Pre-Purchase Inspection Guide | Vanturis",
    description: "A step-by-step guide to inspecting a used luxury motorcoach, Prevost motorhome or entertainer coach before purchase — from Vanturis." },
  { path: "/guides/how-motorcoach-values-are-determined", section: "guide", status: "planned",
    title: "How Motorcoach Values Are Determined | Vanturis",
    description: "The factors that shape luxury motorcoach and Prevost motorhome value — chassis, converter, mileage, condition, documentation and market demand." },
  { path: "/guides/prevost-converters",                   section: "guide", status: "planned",
    title: "Prevost Motorhome Converters Explained | Vanturis",
    description: "Marathon, Liberty, Millennium, Featherlite, Emerald and other Prevost converters — what each specializes in, at a glance from Vanturis." },
  { path: "/guides/prevost-xl-vs-xl2-vs-h3-45",           section: "guide", status: "planned",
    title: "Prevost XL vs XL2 vs H3-45 | Vanturis Comparison",
    description: "Prevost XL, XL2 and H3-45 chassis compared — length, height, drivetrain, era and the coaches typically built on each — from Vanturis." },
  { path: "/guides/luxury-motorcoach-buying-guide",       section: "guide", status: "planned",
    title: "Luxury Motorcoach Buying Guide | Vanturis",
    description: "How to shop, evaluate and buy a premium luxury motorcoach — chassis, converter, documentation and inspection — the Vanturis buying guide." },
  { path: "/guides/12-bunk-vs-14-bunk-entertainer-coach", section: "guide", status: "planned",
    title: "12-Bunk vs 14-Bunk Entertainer Coach | Vanturis",
    description: "How 12-bunk and 14-bunk entertainer coach floorplans differ — bunk layout, lounge configuration, storage and touring use — from Vanturis." },
  { path: "/guides/motorcoach-financing",                 section: "guide", status: "planned",
    title: "Motorcoach & Prevost Financing Guide | Vanturis",
    description: "How financing works for luxury motorcoaches and Prevost motorhomes — lender categories, term ranges, down payments and structure options." },
  { path: "/guides/motorcoach-ownership-records",         section: "guide", status: "planned",
    title: "Motorcoach Ownership Records Explained | Vanturis",
    description: "Why ownership history, service records and documentation change how a premium motorcoach is priced, inspected and transacted — from Vanturis." },
];

/** Convenience filters used by the sitemap generator + tests. */
export const LIVE_INDEXABLE_PAGES = SEO_PAGES.filter(
  (p) => p.status === "live" && p.indexable !== false
);
export const PLANNED_PAGES = SEO_PAGES.filter((p) => p.status === "planned");

export const PAGE_BY_PATH = Object.fromEntries(
  SEO_PAGES.map((p) => [p.path, p])
);
